import { Plus, X } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePurchaseOrderSendRecipients } from '@/features/purchase-order/hooks/use-purchase-order-send-recipients';

type PurchaseOrderSendRecipientsProps = {
  isSending: boolean;
  open: boolean;
  recipients: string[];
  recipientLabels?: Record<string, string>;
  onChange: (nextRecipients: string[]) => void;
};

export function PurchaseOrderSendRecipients({
  isSending,
  open,
  recipients,
  recipientLabels,
  onChange,
}: PurchaseOrderSendRecipientsProps) {
  const { t } = useTranslation('purchaseOrder');
  const {
    form,
    showRecipientInput,
    addRecipient,
    handleRecipientKeyDown,
    removeRecipient,
    handleAddClick,
  } = usePurchaseOrderSendRecipients({
    open,
    recipients,
    onChange,
  });

  return (
    <div className='flex flex-row flex-wrap items-center gap-2'>
      <Label htmlFor='recipient'>{t('sendPoModalRecipient')} : </Label>
      {recipients.map((recipient) => {
        const displayRecipient = recipientLabels?.[recipient] ?? recipient;
        const shouldShowRecipientTooltip = displayRecipient !== recipient;
        return (
          <Badge
            className='max-w-full gap-1 pr-1'
            key={recipient}
            variant='gray'
          >
            {shouldShowRecipientTooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='max-w-[220px] truncate'>
                    {displayRecipient}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{recipient}</TooltipContent>
              </Tooltip>
            ) : (
              <span className='max-w-[220px] truncate'>{displayRecipient}</span>
            )}
            <button
              aria-label={t('delete', { ns: 'common' })}
              className='rounded-sm p-0.5 opacity-75 transition-opacity hover:opacity-100'
              disabled={isSending}
              onClick={() => removeRecipient(recipient)}
              type='button'
            >
              <X className='size-3' />
            </button>
          </Badge>
        );
      })}
      <Button
        aria-label={t('add', { ns: 'common' })}
        disabled={isSending}
        onClick={handleAddClick}
        size='icon'
        type='button'
        variant='outline'
      >
        <Plus />
      </Button>
      {showRecipientInput && (
        <div className='flex w-full min-w-0 flex-col gap-1'>
          <Controller
            control={form.control}
            name='newRecipient'
            render={({ field, fieldState }) => (
              <>
                <Input
                  autoFocus
                  className='h-8 w-full'
                  id='recipient'
                  onKeyDown={handleRecipientKeyDown}
                  placeholder={t('sendPoModalRecipientPlaceholder')}
                  type='email'
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    addRecipient();
                  }}
                />
                {fieldState.error?.message && (
                  <FormErrorMessage
                    error={fieldState.error.message}
                    ns='purchaseOrder'
                  />
                )}
              </>
            )}
          />
        </div>
      )}
    </div>
  );
}
